import styled from 'styled-components';
import React, { useEffect, useState, useRef } from 'react';
import { parsePhoneNumber } from 'react-phone-number-input';
import ReactPhoneInput from 'react-phone-number-input';
import en from 'react-phone-number-input/locale/en.json';
import fr from 'react-phone-number-input/locale/fr.json';
import it from 'react-phone-number-input/locale/it.json';
import pl from 'react-phone-number-input/locale/pl.json';
import es from 'react-phone-number-input/locale/es.json';

import 'react-phone-number-input/style.css';
import { PremiumInputContainer } from '../features/emergencies/ui/inputs/ui/PremiumInputContainer';
import { Invisible } from '../features/emergencies/ui/inputs/ui/Invisible';
import { PremiumLabelBig } from '../features/emergencies/ui/inputs/ui/PremiumLabelBig';
import { PremiumLabelSmall } from '../features/emergencies/ui/inputs/ui/PremiumLabelSmall';
import { LabelAndValue } from 'refactored/features/emergencies/ui/inputs/ui/LabelAndValue';
import { Value } from 'refactored/features/emergencies/ui/inputs/ui/Value';
import { RequiredIndicator } from 'refactored/features/emergencies/ui/inputs/ui/RequiredIndicator';

export const useOutsideClickHook = (callback) => {
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        callback();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [callback]);

  return ref;
};

export const PhoneInputContainer = styled.div`
  input {
    width: 100%;
    outline: none;
    border: none;
    color: var(--grey700, #54708c);
    font-feature-settings: 'clig' off, 'liga' off;
    font-family: Roboto;
    font-size: 14px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 114.286% */
    ::placeholder {
      color: var(--grey-500, #94a6b8);
      opacity: 1; /* Firefox */
    }
    ::-ms-input-placeholder {
      /* Edge 12 -18 */
      color: var(--grey-500, #94a6b8);
    }
  }
  .PhoneInput {
    width: 100%;
  }
`;

const ValueComponentStyled = styled(Value)`
  display: flex;
`;

function ContainerComponent({
  children,
  hasValue,
  label,
  hasError,
  isDisabled,
  isRequired,
  width,
  hasFocus,
  setHasFocus,
}) {
  const ref = useOutsideClickHook(() => {
    setHasFocus(false);
  });

  const triggerFocus = () => {
    setHasFocus(true);
    setTimeout(() => {
      ref.current?.focus();
      if (ref.current) {
        const input = ref.current.querySelector('input');
        input?.focus();
      }
    }, 1);
  };

  const showInput = hasFocus || hasValue;

  return (
    <PremiumInputContainer
      hasError={hasError}
      hasFocus={hasFocus}
      isDisabled={isDisabled}
      onClick={triggerFocus}
      ref={ref}
      width={width}
    >
      {!showInput ? (
        <>
          <PremiumLabelBig>
            {label}
            {isRequired ? <RequiredIndicator /> : null}
          </PremiumLabelBig>
          <Invisible>
            <input
              autoComplete="off"
              onBlur={() => {
                setHasFocus(false);
              }}
              onFocus={triggerFocus}
              readOnly
              type="tel"
            />
          </Invisible>
        </>
      ) : (
        <LabelAndValue>
          <PremiumLabelSmall>
            {label}
            {isRequired ? <RequiredIndicator /> : null}
          </PremiumLabelSmall>
          <ValueComponentStyled>{children}</ValueComponentStyled>
        </LabelAndValue>
      )}
    </PremiumInputContainer>
  );
}

export const DEFAULT_ALLOWED_COUNTRY_CODES = [
  'ES', // Spain
  'AD', // Andorra
  'AT', // Austria
  'BE', // Belgium
  'BG', // Bulgaria
  'CH', // Switzerland
  'CZ', // Czechia
  'DE', // Germany
  'DK', // Denmark
  'DZ', // Algeria
  'EE', // Estonia
  'FI', // Finland
  'FR', // France
  'GB', // UK
  'GR', // Greece
  'HR', // Croatia
  'HU', // Hungary
  'IE', // Ireland
  'IT', // Italy
  'LI', // Liechtenstein
  'LT', // Lithuania
  'LU', // Luxembourg
  'MA', // Morocco
  'ME', // Montenegro
  'MT', // Malta
  'NL', // Netherlands
  'NO', // Norway
  'PL', // Poland
  'PT', // Portugal
  'RO', // Romania
  'RS', // Serbia
  'SE', // Sweden
  'SI', // Slovenia
  'SK', // Slovakia
  'TN', // Tunisia
  'TR', // Turkey
];

const defaultPrefixForNumbersWithoutCountryPrefix = '+34';
export function PhoneInput({
  label,
  name,
  value = '',
  hasError,
  handleChangeValue,
  isDisabled,
  width,
  lang = 'en',
  isRequired,
  allowedCountries = DEFAULT_ALLOWED_COUNTRY_CODES,
}) {
  const [hasFocus, setHasFocus] = useState(false);
  const [, setIsSelectOpen] = useState(false);

  let formatedValue = value;
  try {
    // convert 0034 to +34 to show the flag
    if (formatedValue.startsWith('0034')) {
      formatedValue = formatedValue.replace('0034', '+34');
    }
    // convert phone number without country prefix to one with country prefix
    if (
      formatedValue.length > 5 &&
      !formatedValue.startsWith('+') &&
      !formatedValue.startsWith('00')
    ) {
      formatedValue = `${defaultPrefixForNumbersWithoutCountryPrefix}${formatedValue}`;
    }
  } catch (error) {
    formatedValue = value;
  }

  const parsedPhone = parsePhoneNumber(formatedValue);
  const phoneCountry = parsedPhone?.country;
  const shouldDisplayCountryPrefix =
    !phoneCountry || !allowedCountries.includes(phoneCountry);

  let translatedCountryLabels = en;
  if (lang === 'es') translatedCountryLabels = es;
  else if (lang === 'fr') translatedCountryLabels = fr;
  else if (lang === 'it') translatedCountryLabels = it;
  else if (lang === 'pl') translatedCountryLabels = pl;
  else translatedCountryLabels = en;

  const hasValue = Boolean(formatedValue) && formatedValue !== '';

  let allowedCountriesProps;
  if (typeof allowedCountries !== 'undefined') {
    allowedCountriesProps = {
      countries: allowedCountries,
    };
  } else {
    allowedCountriesProps = {};
  }

  const handleBlur = (event) => {
    event.preventDefault();
    const relatedTarget = event.relatedTarget;

    // Check if we're clicking on the country select or its options
    const isCountrySelect = relatedTarget?.className.includes(
      'PhoneInputCountrySelect'
    );
    const isCountryOption = relatedTarget?.className.includes(
      'PhoneInputCountrySelectOption'
    );

    if (!relatedTarget || (!isCountrySelect && !isCountryOption)) {
      setTimeout(() => {
        setHasFocus(false);
        setIsSelectOpen(false);
      }, 100);
    }
  };

  useEffect(() => {
    // Prevent select from closing automatically
    const handleClick = (e) => {
      const target = e.target;
      if (target.closest('.PhoneInputCountrySelect')) {
        e.stopPropagation();
      }
    };

    document.addEventListener('click', handleClick, true);
    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, []);

  return (
    <ReactPhoneInput
      autoComplete="off"
      defaultCountry="ES"
      {...allowedCountriesProps}
      containerComponent={ContainerComponent}
      containerComponentProps={{
        hasValue,
        label,
        hasError,
        isDisabled,
        width,
        isRequired,
        hasFocus,
        setHasFocus,
      }}
      countrySelectProps={{
        onClick: () => {
          setIsSelectOpen(true);
          setHasFocus(true);
        },
        className: 'PhoneInputCountrySelect',
      }}
      disabled={isDisabled}
      id={name}
      initialValueFormat={shouldDisplayCountryPrefix ? undefined : 'national'}
      labels={translatedCountryLabels}
      name={name}
      onBlur={handleBlur}
      onChange={(newValue) => {
        if (handleChangeValue) {
          if (newValue) handleChangeValue(name, newValue.toString());
          else {
            handleChangeValue(name, '');
          }
        }
      }}
      value={formatedValue}
    />
  );
}
